"use client"

import { useEffect, useState } from "react"
import api, { getApiError } from "@/lib/api"
import { useAuth } from "@/lib/auth-context"
import { readLocalDemoBranch, saveLocalDemoBranch } from "@/lib/branch-selection"
import { Button } from "@/components/ui/button"
import { FormContainer } from "@/components/ui/form-container"

type BranchRow = { id: number; name: string }

export function ShopLocalBranchDialog() {
  const { user } = useAuth()
  const [branches, setBranches] = useState<BranchRow[]>([])
  const [mainId, setMainId] = useState<number>(0)
  const [picked, setPicked] = useState("")
  const [error, setError] = useState("")
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (!user?.localDemoShop || user.tenantId !== 1 || user.features?.multiBranch !== true) return
    if (!user.isTenantAdmin) return
    if (readLocalDemoBranch()) return
    let cancelled = false
    api.get("/api/branches").then(({ data }) => {
      if (cancelled) return
      const list = (data.branches ?? []) as BranchRow[]
      setBranches(list)
      setMainId(Number(data.mainBranchId) || list[0]?.id || 0)
      setPicked(String(data.activeBranchId || data.mainBranchId || list[0]?.id || ""))
      setOpen(list.length > 0)
    }).catch((err) => {
      if (!cancelled) setError(getApiError(err, "Could not load branches"))
    })
    return () => { cancelled = true }
  }, [user?.id, user?.localDemoShop, user?.tenantId, user?.features?.multiBranch, user?.isTenantAdmin])

  if (!open) {
    if (!error) return null
    return <p className="px-4 py-2 text-sm text-destructive">{error}</p>
  }

  function confirm() {
    const branch = branches.find((row) => String(row.id) === picked)
    if (!branch) return
    saveLocalDemoBranch(branch, mainId || branch.id)
    setOpen(false)
    window.location.assign("/dashboard")
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <FormContainer size="sm" className="rounded-xl border bg-background p-5 shadow-xl">
        <h2 className="text-base font-semibold">Which branch should this shop load?</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Demo tenant only. This computer will use that branch from now on. You will not be asked again.
        </p>
        <label className="mt-4 grid gap-1 text-sm">
          Branch
          <select
            className="h-9 w-full rounded-md border bg-background px-2"
            value={picked}
            onChange={(event) => setPicked(event.target.value)}
            aria-label="Branch to load locally"
          >
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>{branch.name}</option>
            ))}
          </select>
        </label>
        <Button className="mt-4 w-full" type="button" onClick={confirm} disabled={!picked}>
          Load this branch
        </Button>
      </FormContainer>
    </div>
  )
}
